import { ProductLimits } from 'src/app/models/product-limits.model';

/**
 * Quantity produced by the recipes (CQ/LQ/EQ/TCQ) per local-product code —
 * the code an admin picks in the "Local Product Quantity" PDF tool.
 *
 * Unlike buildCcDesignLines (which attributes each recipe to the one
 * category-content image it is the most specific match for), this indexes by
 * the code string the recipe lines actually carry (CQ/TCQ: `code`, LQ:
 * `codeLimit`), since the PDF tool binds to a product code, not a
 * category-content id. Codes are
 * normalised (trim + uppercase) on both sides because the join is a raw
 * string and LQ stores its numbers as strings too.
 *
 * Each code's total is rounded half-up to an integer once, after summation —
 * the workshop consumes whole pieces.
 */
export function buildCodeQuantities(
  productLimits: ProductLimits[]
): { [code: string]: number } {
  const totals: { [code: string]: number } = {};

  (productLimits || []).forEach(limit => {
    (limit.limits || []).forEach((line: any) => {
      const code = normalizeCode(line.code ?? line.codeLimit);
      if (!code) { return; }

      const value = Number(line.quantityLimit ?? line.quantity);
      if (isNaN(value)) { return; }

      totals[code] = (totals[code] || 0) + value;
    });
  });

  Object.keys(totals).forEach(code => {
    totals[code] = Math.round(totals[code]);
  });

  return totals;
}

export function normalizeCode(code: string | undefined | null): string {
  return (code ?? '').trim().toUpperCase();
}

export function resolveFirstCodeQuantity(
  codes: string[] | undefined,
  legacyCode: string | undefined,
  quantities: { [code: string]: number }
): string {
  const candidates = codes?.length ? codes : [legacyCode];

  for (const candidate of candidates) {
    const code = normalizeCode(candidate);
    if (code && Object.prototype.hasOwnProperty.call(quantities, code)) {
      return String(quantities[code]);
    }
  }

  return '';
}

/**
 * The code of the first local product in `codes` that this configuration
 * actually produces — the "Local Product Code" PDF tool (id 14).
 *
 * Same walk and same "produced" test as resolveFirstCodeQuantity (presence of
 * the code in `quantities`, so a legitimate zero still counts), but it outputs
 * the product's code instead of its quantity. The code is normalised (trim +
 * uppercase) so the rendered value is stable regardless of how the admin typed
 * it when binding the tool.
 *
 * Empty (not a placeholder) when nothing matches, matching every other value
 * tool.
 */
export function resolveFirstCodeName(
  codes: string[] | undefined,
  quantities: { [code: string]: number }
): string {
  for (const candidate of codes || []) {
    const code = normalizeCode(candidate);
    if (code && Object.prototype.hasOwnProperty.call(quantities, code)) {
      return code;
    }
  }

  return '';
}
